import { z } from 'zod'
import { oc } from '@orpc/contract'

// PD-1416 — Care Journeys (Care Management dashboard).
//
// A "care journey" is a PATIENT, not a visit: the primary KPI counts unique
// patients with at least one open, overdue or future care action. Each journey
// carries one row per visit so the table can expand a multi-visit patient
// without losing which visit produced which action.

/** Status colour. Always paired with `label` — the UI must never rely on colour alone. */
export const JOURNEY_TONES = ['green', 'blue', 'red', 'gray'] as const
export const journeyToneSchema = z.enum(JOURNEY_TONES)

/**
 * Lifecycle of a single tracked action, independent of which column shows it.
 * `not-required` and `not-available` both render gray but mean different things
 * (nothing was ever asked for vs. asked for but nothing to show yet).
 */
export const JOURNEY_STATES = [
  'completed',
  'in-progress',
  'future',
  'overdue',
  'not-required',
  'not-available',
  // Deliberately reached and deliberately not done — a practitioner declined
  // the patient email at triage. Terminal like `completed`, but it is not a
  // success, so it must not read as one.
  'declined',
] as const
export const journeyStateSchema = z.enum(JOURNEY_STATES)

/**
 * One rendered cell: human-readable text plus the machine state that drove it.
 * `at` is the applicable date/time and `org` the applicable organization, both
 * per the ticket's "each status must display the applicable date/time and
 * organization where available".
 */
export const journeyCellSchema = z.object({
  label: z.string(),
  state: journeyStateSchema,
  tone: journeyToneSchema,
  at: z.string().nullable(),
  org: z.string().nullable(),
  /**
   * Secondary line under the label — a duration ("6 days"), or the specific
   * action behind a summarised status. Durations live here rather than in
   * `label` so a status reads as a status, per the approved design.
   */
  detail: z.string().nullable(),
})

export const journeyRefSchema = z.object({ reference: z.string(), display: z.string() })

/** Which KPI card a patient is counted under. Categories deliberately overlap. */
export const JOURNEY_KPIS = [
  'active',
  'awaiting-results',
  'results-need-followup',
  'completed',
] as const
export const journeyKpiSchema = z.enum(JOURNEY_KPIS)

/** Why a patient is in a KPI — surfaced verbatim in the KPI detail drawer. */
export const journeyReasonSchema = z.object({
  kpi: journeyKpiSchema,
  /** e.g. "Future CBC due Nov 6", "Awaiting lipid panel result for 6 days". */
  text: z.string(),
  state: journeyStateSchema,
  tone: journeyToneSchema,
  /** Due date, or the instant the wait started. */
  at: z.string().nullable(),
  /** Whole days waited/overdue, when the reason is duration-shaped. */
  days: z.number().int().nullable(),
})

export const VISIT_TYPES = ['initial', 'follow-up', 'consultation', 'other'] as const
export const visitTypeSchema = z.enum(VISIT_TYPES)

export const ACTION_TYPES = ['lab', 'imaging', 'referral', 'follow-up-visit', 'other'] as const
export const actionTypeSchema = z.enum(ACTION_TYPES)

// No separate 'sent' state: once a requisition is out the door the meaningful
// question is whether its result is back, so a sent-and-unanswered order IS
// 'awaiting-result'. Offering both would put an option in the filter drawer
// that can never match a row.
export const ORDER_STATUSES = [
  'not-sent',
  'awaiting-result',
  'result-received',
  'not-required',
] as const
export const orderStatusSchema = z.enum(ORDER_STATUSES)

export const FOLLOWUP_STATUSES = [
  'needed',
  'not-scheduled',
  'scheduled',
  'completed',
  'not-required',
] as const
export const followUpStatusSchema = z.enum(FOLLOWUP_STATUSES)

export const JOURNEY_STATUS_FILTERS = ['open', 'overdue', 'future', 'completed'] as const
export const journeyStatusFilterSchema = z.enum(JOURNEY_STATUS_FILTERS)

/** One visit of one patient — the ticket's nine columns, minus Patient (on the group). */
export const journeyVisitSchema = z.object({
  encounterId: z.string().nullable(),
  appointmentId: z.string().nullable(),
  /** ISO instant the visit occurred (or is scheduled for). */
  visitDate: z.string().nullable(),
  visitType: visitTypeSchema,
  /** Column: Visit. */
  visit: journeyCellSchema,
  practitioner: journeyRefSchema.nullable(),
  /** Column: Practitioner Request — what the practitioner asked for at this visit. */
  request: journeyCellSchema,
  /** Column: Order. */
  order: journeyCellSchema,
  /** Column: Result. */
  result: journeyCellSchema,
  /** Column: Patient Notified. */
  notified: journeyCellSchema,
  /** Column: Follow-up / Next Action. */
  nextAction: journeyCellSchema,
  /** Column: Owner — the assigned care coordinator. */
  owner: journeyRefSchema.nullable(),
  /** Derived filter facets for this visit. */
  actionType: actionTypeSchema,
  orderStatus: orderStatusSchema,
  followUpStatus: followUpStatusSchema,
  /** True when nothing on this visit remains open, overdue or future. */
  isComplete: z.boolean(),
  /** True when any action on this visit is past due. */
  hasOverdue: z.boolean(),
  /**
   * Whole days past a FOLLOW-UP TASK's due date, or null when nothing is late.
   * Task-derived only: orders and results carry no due date, so inventing an
   * "overdue by N" for them would be fabricating a deadline nobody set. Their
   * cells show the date they carry instead.
   */
  overdueDays: z.number().int().nullable(),
  orderId: z.string().nullable(),
  /** Care Queue board item backing the follow-up, for deep-linking. */
  boardItemId: z.string().nullable(),
  boardId: z.string().nullable(),
})

export const careJourneySchema = z.object({
  patientId: z.string(),
  patientName: z.string(),
  /** Newest first, per the ticket. */
  visits: z.array(journeyVisitSchema),
  visitCount: z.number().int(),
  /** Patient-level red indicator: any visit has an overdue action. */
  hasOverdue: z.boolean(),
  /** The worst `overdueDays` across this patient's visits, for the card's urgency chip. */
  overdueDays: z.number().int().nullable(),
  /** KPI cards this patient is counted under. */
  kpis: z.array(journeyKpiSchema),
  /** Populates the KPI detail drawer. */
  reasons: z.array(journeyReasonSchema),
})

export const careJourneyKpisSchema = z.object({
  /** Unique patients with any open, overdue or future action. */
  active: z.number().int(),
  /** Unique patients with a sent order whose result has not arrived. */
  awaitingResults: z.number().int(),
  /** Subset of the above that is past the expected turnaround. */
  awaitingResultsOverdue: z.number().int(),
  /** Unique patients with a received result still needing practitioner follow-up. */
  resultsNeedFollowUp: z.number().int(),
  /** Unique patients whose every visit in scope has nothing left outstanding. */
  completed: z.number().int(),
})

export const careJourneyFiltersSchema = z.object({
  /** Defaults to the last 30 days when both bounds are omitted. */
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
  /**
   * Patients picked from the filter drawer's dropdown, by id.
   *
   * Deliberately not a free-text name query: FHIR display names contain
   * doubled spaces (an empty middle name), prefixes like "Mrs.", and suffixes,
   * so substring matching silently failed on the exact full name a user would
   * type — "Simon Brown" never matched "Simon  Brown".
   */
  patientIds: z.array(z.string()).optional(),
  practitionerIds: z.array(z.string()).optional(),
  ownerIds: z.array(z.string()).optional(),
  statuses: z.array(journeyStatusFilterSchema).optional(),
  actionTypes: z.array(actionTypeSchema).optional(),
  orderStatuses: z.array(orderStatusSchema).optional(),
  followUpStatuses: z.array(followUpStatusSchema).optional(),
  visitTypes: z.array(visitTypeSchema).optional(),
  /**
   * One-click exception filter; off by default.
   *
   * Accepts the string forms as well as a real boolean: this route is a GET, so
   * its input travels in the query string and `true` arrives as `'true'`. A
   * bare `z.boolean()` rejected that and 400'd the whole request, taking the
   * dashboard down instead of filtering it (PD-1466). Every other boolean input
   * in this codebase sits on a POST/PATCH body, where JSON keeps the type — this
   * is the only one that has to survive the wire as text.
   *
   * Deliberately not `z.coerce.boolean()`: that is `Boolean(value)`, which turns
   * the string `'false'` into `true`.
   */
  needsAttentionOnly: z
    .union([z.boolean(), z.enum(['true', 'false']).transform((value) => value === 'true')])
    .optional(),
})

/**
 * KPI counts are computed over the FILTERED set, so the cards and the table
 * always agree (ticket: "Apply filters updates both the patient table and KPI
 * counts"). `filterOptions` are the caller's org-scoped practitioner and owner
 * lists, so the filter drawer needs no extra round-trip and can never offer a
 * name from another organization.
 */
export const careJourneys = oc
  .route({ method: 'GET', path: '/v2/care-journeys' })
  .input(careJourneyFiltersSchema)
  .output(
    z.object({
      journeys: z.array(careJourneySchema),
      kpis: careJourneyKpisSchema,
      filterOptions: z.object({
        practitioners: z.array(journeyRefSchema),
        owners: z.array(journeyRefSchema),
        /** Patients in scope, for the drawer's picker. Org-scoped by construction. */
        patients: z.array(journeyRefSchema),
      }),
      /** Bounds actually applied, so the UI can show the effective date range. */
      appliedDateFrom: z.string(),
      appliedDateTo: z.string(),
      /** True when the scan hit its cap and the result may be incomplete. */
      truncated: z.boolean(),
    }),
  )
